import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { searchGames } from '@/api/games'
import { addToWishlist, fetchWishlist, fetchPlaying, fetchPlayed } from '@/api/wishlist'
import { useDemoteToWishlist } from '@/hooks/useDemoteToWishlist'
import { PriceTag } from '@/components/ui/PriceTag'
import { Icon } from '@/components/ui/Icon'

interface AddGameModalProps {
  groupId: string
  existingGameIds: Set<string>
  existingLabel?: string
  onClose: () => void
  /** If provided, called instead of the default add-to-wishlist behavior. */
  onAdd?: (gameId: string) => void
  /** External pending state when onAdd is provided. */
  isAdding?: boolean
  title?: string
}

const badgeStyle: React.CSSProperties = {
  fontSize: 11, padding: '2px 6px', borderRadius: 3, flexShrink: 0,
  color: 'var(--accent)', background: 'var(--bg-3)',
}

export function AddGameModal({
  groupId,
  existingGameIds,
  existingLabel = 'On wishlist',
  onClose,
  onAdd,
  isAdding = false,
  title = 'Add to wishlist',
}: AddGameModalProps) {
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set())
  const inputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300)
    return () => clearTimeout(timer)
  }, [query])

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose])

  const { data: results = [], isFetching } = useQuery({
    queryKey: ['game-search', debouncedQuery],
    queryFn: () => searchGames(debouncedQuery),
    enabled: debouncedQuery.length >= 2,
  })

  const { data: wishlistData } = useQuery({
    queryKey: ['wishlist', groupId],
    queryFn: () => fetchWishlist(groupId),
  })
  const { data: playingEntries = [] } = useQuery({
    queryKey: ['playing', groupId],
    queryFn: () => fetchPlaying(groupId),
  })
  const { data: playedEntries = [] } = useQuery({
    queryKey: ['played', groupId],
    queryFn: () => fetchPlayed(groupId),
  })

  const wishlistIds = useMemo(
    () => new Set((wishlistData?.entries ?? []).map((e) => e.gameId)),
    [wishlistData],
  )
  const playingIds = useMemo(() => new Set(playingEntries.map((e) => e.gameId)), [playingEntries])
  const playedIds = useMemo(() => new Set(playedEntries.map((e) => e.gameId)), [playedEntries])

  const { mutate: addGame, isPending: isAddPending, variables: addPendingId } = useMutation<void, Error, string>({
    mutationFn: async (gameId) => {
      if (onAdd) {
        onAdd(gameId)
      } else {
        await addToWishlist(groupId, gameId)
      }
    },
    onSuccess: (_data, gameId) => setAddedIds((prev) => new Set([...prev, gameId])),
    onSettled: () => {
      if (!onAdd) {
        queryClient.invalidateQueries({ queryKey: ['wishlist', groupId] })
        queryClient.invalidateQueries({ queryKey: ['playing', groupId] })
        queryClient.invalidateQueries({ queryKey: ['played', groupId] })
      }
    },
  })

  const { mutate: moveToWishlist, isPending: isMovePending, variables: movePendingId } = useDemoteToWishlist(groupId)

  const anyPending = isAddPending || isMovePending || isAdding
  const moveLabel = title.replace(/^Add/, 'Move')

  return createPortal(
    <div className="cg-modal-backdrop" onClick={onClose}>
      <div className="cg-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cg-modal-header">
          <h2>{title}</h2>
          <button className="cg-icon-btn cg-modal-close-x" onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: '10px 12px 6px' }}>
          <div className="cg-search inline">
            <Icon name="search" size={14} />
            <input
              ref={inputRef}
              placeholder="Search Steam games…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button className="cg-search-clear" onClick={() => setQuery('')}>
                <Icon name="x" size={10} />
              </button>
            )}
          </div>
        </div>

        <div className="cg-modal-list" style={{ minHeight: 80 }}>
          {isFetching && (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--fg-3)', fontSize: 13 }}>
              Searching…
            </div>
          )}
          {!isFetching && debouncedQuery.length >= 2 && results.length === 0 && (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--fg-3)', fontSize: 13 }}>
              No matches
            </div>
          )}
          {!isFetching && results.map((result) => {
            const id = String(result.appId)
            const inTargetList = existingGameIds.has(id) || addedIds.has(id)
            const isPlaying = !inTargetList && playingIds.has(id)
            const isPlayed = !inTargetList && !isPlaying && playedIds.has(id)
            const isOnWishlist = !inTargetList && !isPlaying && !isPlayed && wishlistIds.has(id)

            const thisRowPending = (isAddPending && addPendingId === id) || (isMovePending && movePendingId === id)
            const useMoveToWishlist = !onAdd && (isPlaying || isPlayed)
            const useMoveLabel = useMoveToWishlist || ((isOnWishlist || isPlayed || isPlaying) && !!onAdd)

            return (
              <div key={result.appId} className="cg-modal-row">
                {result.headerImageUrl ? (
                  <img
                    src={result.headerImageUrl}
                    alt=""
                    style={{ width: 72, height: 40, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
                  />
                ) : (
                  <div style={{ width: 72, height: 40, background: 'var(--bg-3)', borderRadius: 4, flexShrink: 0 }} />
                )}
                <div className="cg-modal-row-body">
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--fg-0)', marginBottom: 2 }}>
                    {result.title}
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <PriceTag game={result} compact />
                    {inTargetList && <span style={badgeStyle}>{existingLabel}</span>}
                    {isPlaying && <span style={badgeStyle}>Playing</span>}
                    {isPlayed && <span style={badgeStyle}>Played</span>}
                    {isOnWishlist && <span style={badgeStyle}>On wishlist</span>}
                  </div>
                </div>
                {!inTargetList && (
                  <button
                    className={`cg-btn${useMoveLabel ? '' : ' cg-btn-primary'}`}
                    style={{ fontSize: 12, padding: '5px 12px', flexShrink: 0 }}
                    disabled={anyPending}
                    onClick={() => {
                      if (useMoveToWishlist) {
                        moveToWishlist(id, { onSuccess: () => setAddedIds((prev) => new Set([...prev, id])) })
                      } else {
                        addGame(id)
                      }
                    }}
                  >
                    {thisRowPending
                      ? <span className="cg-spinner" style={{ width: 12, height: 12 }} />
                      : useMoveLabel ? moveLabel : 'Add'}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>,
    document.body,
  )
}
